/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from "react";
import apiService from "services/apiService";
import { Send, Plus, Trash2, MessageSquare, Menu, ArrowLeft } from "lucide-react";
import GenericModal from "components/Modal";
import DeletePopup from "components/DeletePopup/deletePopup";
import { useAppContext } from "contexts";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useToaster } from "contexts/toasterContext";
import { useNavigate } from "react-router-dom";

// Import your custom IST formatter
import { convertUtcToIst } from "util/Util";

const MessageRenderer = ({ content }: { content: string }) => (
    <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
            code({ className, children, ...props }: any) {
                const match = /language-(\w+)/.exec(className || '');
                return match ? (
                    <SyntaxHighlighter style={vscDarkPlus} language={match[1]} PreTag="div" {...props}>
                        {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                ) : (
                    <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded font-mono text-sm" {...props}>
                        {children}
                    </code>
                );
            },
            table: ({ children }) => (
                <div className="overflow-x-auto my-4">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border">
                        {children}
                    </table>
                </div>
            ),
            p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
            ul: ({ children }) => <ul className="list-disc pl-5 mb-4 space-y-1">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal pl-5 mb-4 space-y-1">{children}</ol>,
        }}
    >
        {content}
    </ReactMarkdown>
);

const formatSessionTime = (dateString?: string) => {
    if (!dateString) return "";
    const safeString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const istObj = convertUtcToIst(safeString);
    return istObj.full !== "N/A" ? `${istObj.date}, ${istObj.time}` : "";
};

export default function AIChatPage() {
    const { user } = useAppContext();
    const { addToast } = useToaster();
    const navigate = useNavigate();
    const projects = user?.userProjectWithAssignedDate || [];

    const [sessions, setSessions] = useState<any[]>([]);
    const [activeSession, setActiveSession] = useState<number | null>(null);
    const [messages, setMessages] = useState<any[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [followUps, setFollowUps] = useState<string[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const currentSessionRef = useRef<number | null>(null);

    const [chatScope, setChatScope] = useState<"ALL" | "SELECTED">("ALL");
    const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
    const [newChatTitle, setNewChatTitle] = useState("");
    const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState<number | null>(null);
    const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);
    const [creatingChat, setCreatingChat] = useState(false);

    const SmallSpinner = () => (
        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
    );

    useEffect(() => {
        fetchSessions();
    }, []);

    useEffect(() => {
        currentSessionRef.current = activeSession;
        if (activeSession) {
            setMessages([]);
            setInput("");
            setFollowUps([]);
            setLoading(false);
            fetchHistory(activeSession);
        } else {
            setMessages([]);
            setFollowUps([]);
            setInput("");
        }
    }, [activeSession]);

    useEffect(() => {
        // block: "nearest" keeps the auto-scroll inside the conversation pane
        // instead of letting it scroll the surrounding page as well
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [messages, loading]);

    const fetchSessions = async () => {
        try {
            const res = await apiService.get("api/pi/chat/sessions");
            setSessions(res.data || []);
        } catch (error: any) {
            console.error("Failed to load sessions", error);
            addToast(error?.response?.data?.message || "Failed to load chat sessions.", "error");
        }
    };

    const fetchHistory = async (sessionId: number) => {
        try {
            setHistoryLoading(true);
            const res = await apiService.get(`api/pi/chat/sessions/${sessionId}/messages`);
            if (currentSessionRef.current === sessionId) {
                const history = res.data || [];
                setMessages(history);
                const lastAssistantMessage = [...history].reverse().find((msg) => msg.role === "ASSISTANT");
                setFollowUps(Array.isArray(lastAssistantMessage?.followUps) ? lastAssistantMessage.followUps : []);
            }
        } catch (error: any) {
            addToast(error?.response?.data?.message || "Failed to load chat history.", "error");
        } finally {
            if (currentSessionRef.current === sessionId) {
                setHistoryLoading(false);
            }
        }
    };

    const handleCreateChat = async () => {
        try {
            setCreatingChat(true);
            const payload = chatScope === "ALL" ? { title: newChatTitle.trim(), scope: "ALL" } : { title: newChatTitle.trim(), scope: "SELECTED", projectIds: selectedProjectIds };
            const response = await apiService.post("api/pi/chat/sessions", payload);
            const newSession = response.data;
            addToast(response.data?.message || "Chat created successfully.", "success");
            await fetchSessions();
            setActiveSession(newSession.id);
            closeNewChatModal();
            if (newSession.messages?.length > 0) setMessages(newSession.messages);
            if (!isSidebarOpen) setIsSidebarOpen(true);
        } catch (error: any) {
            addToast(error?.response?.data?.message || "Failed to create chat session.", "error");
        } finally {
            setCreatingChat(false);
        }
    };

    const confirmDelete = async () => {
        if (!sessionToDelete) return;
        try {
            setDeletingSessionId(sessionToDelete);
            const response = await apiService.delete(`api/pi/chat/sessions/${sessionToDelete}`);
            addToast(response.data?.message || "Chat deleted successfully.", "success");
            await fetchSessions();
            if (activeSession === sessionToDelete) setActiveSession(null);
            setOpenDeleteModal(false);
            setSessionToDelete(null);
        } catch (error: any) {
            addToast(error?.response?.data?.message || "Failed to delete chat session.", "error");
        } finally {
            setDeletingSessionId(null);
        }
    };

    const sendMessage = async (messageText?: string) => {
        const question = messageText || input;
        const sessionIdAtRequest = activeSession;
        if (!question.trim() || !sessionIdAtRequest) return;
        try {
            setMessages((prev) => [...prev, { role: "USER", content: question }]);
            setInput("");
            setLoading(true);
            setFollowUps([]);
            const response = await apiService.post(`api/pi/chat/sessions/${sessionIdAtRequest}/messages`, { content: question });
            if (currentSessionRef.current === sessionIdAtRequest) {
                setMessages((prev) => [...prev, { role: "ASSISTANT", content: response?.data?.answer || "No response received." }]);
                setFollowUps(response?.data?.followUps || response?.data?.followUpQuestions || []);
            }
        } catch (error: any) {
            addToast(error?.response?.data?.message || "Failed to send message.", "error");
        } finally {
            if (currentSessionRef.current === sessionIdAtRequest) {
                setLoading(false);
            }
        }
    };

    const openNewChatModal = () => {
        setNewChatTitle("");
        setSelectedProjectIds([]);
        setChatScope("ALL");
        setIsNewChatModalOpen(true);
    };

    const closeNewChatModal = () => {
        setIsNewChatModalOpen(false);
    };

    const toggleProjectSelection = (projectId: number) => {
        setSelectedProjectIds((prev) => prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (!loading && input.trim()) {
                sendMessage();
            }
        }
    };

    const isCreateDisabled = !newChatTitle.trim() || (chatScope === "SELECTED" && selectedProjectIds.length === 0);

    const ButtonSpinner = () => (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
    );

    const currentSession = sessions.find((s) => s.id === activeSession);

    /*
     * NOTE (layout): the shell below uses a definite height instead of the
     * previous `h-full min-h-[calc(100vh-100px)]`. A min-height let the shell
     * grow past the viewport as follow-ups piled up, and the shell's
     * `overflow-hidden` then clipped whatever fell below it — that is how the
     * message input disappeared and the conversation stopped scrolling. With a
     * definite height the input stays pinned on screen and only the inner panes
     * scroll (see `min-h-0` on the flex columns and the capped follow-up strip).
     *
     * NOTE (height): the shell now uses `h-full` instead of the hard-coded
     * `h-[calc(100vh-100px)]`. The parent <Outlet /> wrapper in
     * components/SideBar is already a bounded `flex-1 min-h-0` pane, so filling
     * it removes the ~100px gap below the chat while still respecting the plan
     * banner and the mobile top bar, with no magic numbers and no page scroll.
     */
    return (
        <div className="flex h-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm transition-colors duration-300">
            <div className={`bg-gray-50 dark:bg-gray-800 flex flex-col shrink-0 transition-all duration-300 ease-in-out overflow-hidden border-gray-200 dark:border-gray-700 ${isSidebarOpen ? "w-80 border-r" : "w-0 border-r-0"}`}>
                <div className="w-80 flex flex-col h-full">
                    {/* UPDATED: Sidebar Header fixed height and standardized bottom border */}
                    <div className="h-[76px] px-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-800/80 shrink-0">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate("/admin/projects")}
                                className="p-1.5 -ml-1.5 text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                title="Back to Projects"
                            >
                                <ArrowLeft size={18} />
                            </button>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Chats</h2>
                        </div>
                        <button onClick={openNewChatModal} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/10 text-blue-600 dark:bg-blue-600/20 dark:text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-600/20 dark:hover:bg-blue-600/30 transition-colors">
                            <Plus size={16} /> New
                        </button>
                    </div>
                    {/* min-h-0: a flex child must be allowed to shrink before it can scroll */}
                    <div className="flex-1 min-h-0 overflow-y-auto">
                        {sessions.length === 0 ? (
                            <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No chats yet. Start a new conversation!</div>
                        ) : (
                            sessions.map((session) => (
                                <div key={session.id} onClick={() => setActiveSession(session.id)} className={`group p-4 flex justify-between items-start border-b border-transparent dark:border-gray-800/50 transition-all duration-200 ${deletingSessionId === session.id ? "opacity-50 pointer-events-none" : "cursor-pointer"} ${activeSession === session.id ? "bg-white dark:bg-gray-700/50 shadow-sm dark:shadow-none" : "hover:bg-gray-100 dark:hover:bg-[#1a1b26]"}`}>
                                    <div className="flex-1 min-w-0 pr-4">
                                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate mb-1.5">{session.title || "Untitled Chat"}</div>

                                        <div
                                            className="text-xs text-gray-600 dark:text-gray-400 truncate"
                                            title={session.projectNames && session.projectNames.length > 0 ? session.projectNames.join(", ") : session.scope === "ALL" ? "All projects" : "Selected projects"}
                                        >
                                            {session.projectNames && session.projectNames.length > 0 ? session.projectNames.join(", ") : session.scope === "ALL" ? "All projects" : "Selected projects"}
                                        </div>

                                        <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                            {formatSessionTime(session.createdAt)}
                                        </div>
                                    </div>
                                    <div className="shrink-0 mt-0.5 w-6 flex justify-center">
                                        {deletingSessionId === session.id ? <SmallSpinner /> : (
                                            <button className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-all" onClick={(e) => { e.stopPropagation(); setSessionToDelete(session.id); setOpenDeleteModal(true); }}>
                                                <Trash2 size={15} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 relative min-w-0 min-h-0">
                {!activeSession ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-500">
                        <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700/50 rounded-full flex items-center justify-center mb-6">
                            <MessageSquare size={40} className="text-gray-400 dark:text-gray-500" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Ask anything about your project documents</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">Create a new chat session to get started. The AI will answer based on your specific project data.</p>
                    </div>
                ) : (
                    <>
                        {/* UPDATED: Main Content Header fixed height to exactly match sidebar header */}
                        <div className="h-[76px] px-5 border-b border-gray-200 dark:border-gray-800/80 bg-white dark:bg-gray-700/50 z-10 shrink-0 flex items-center gap-3">
                            <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 -ml-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-600 rounded-lg transition-colors" title="Toggle sidebar">
                                <Menu size={20} />
                            </button>
                            <div className="flex flex-col min-w-0 justify-center">
                                <h2 className="font-semibold text-gray-900 dark:text-white truncate">{currentSession?.title || "Chat"}</h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{currentSession?.projectNames?.length ? currentSession.projectNames.join(", ") : currentSession?.scope === "ALL" ? "All Projects" : "Selected Projects"}</p>
                            </div>
                        </div>

                        {/* The conversation is the pane that scrolls; min-h-0 lets it
                            shrink instead of pushing the follow-ups and input away */}
                        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6">
                            {historyLoading ? (
                                <div className="flex items-center justify-center h-full min-h-[300px]"><p className="text-sm font-medium text-gray-500 dark:text-gray-400">Loading conversation...</p></div>
                            ) : (
                                <>
                                    {messages.map((msg, idx) => (
                                        <div key={idx} className={`flex ${msg.role === "USER" ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[85%] px-5 py-4 rounded-2xl text-[15px] shadow-sm ${msg.role === "USER" ? "bg-blue-600 text-white rounded-br-none" : "bg-white border border-gray-100 text-gray-800 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 rounded-bl-none shadow-md"}`}>
                                                {msg.role === "USER" ? msg.content : <MessageRenderer content={msg.content} />}
                                            </div>
                                        </div>
                                    ))}
                                    {loading && (
                                        <div className="flex justify-start">
                                            <div className="bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700 rounded-2xl rounded-bl-none px-5 py-4 shadow-sm">
                                                <div className="flex items-center gap-1">
                                                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                                                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                                                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div ref={messagesEndRef} />
                                </>
                            )}
                        </div>

                        {/* Follow-ups are capped and scroll on their own: an unbounded
                            strip used to eat the whole column, collapsing the
                            conversation and pushing the input out of view */}
                        {followUps.length > 0 && (
                            <div className="px-6 py-4 flex flex-wrap gap-2 bg-white dark:bg-gray-800 shrink-0 max-h-[30%] overflow-y-auto">
                                {followUps.map((question, index) => (
                                    <button key={index} type="button" onClick={() => sendMessage(question)} disabled={loading} className="px-4 py-2 text-sm rounded-full bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 dark:bg-gray-800 dark:border-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors shadow-sm text-left">
                                        {question}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="p-4 bg-white dark:bg-gray-800 shrink-0">
                            <form className="flex gap-2 max-w-4xl mx-auto relative" onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
                                <textarea
                                    value={input}
                                    rows={1}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Ask anything..."
                                    disabled={loading}
                                    className="flex-1 py-3.5 pl-4 pr-12 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white dark:border-gray-700 dark:bg-gray-800 dark:focus:bg-[#1a1b26] dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:outline-none disabled:opacity-50 transition-colors shadow-sm resize-none overflow-y-auto h-[56px] min-h-[56px] max-h-[56px]"
                                />
                                <button type="submit" disabled={loading || !input.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:bg-gray-300 dark:disabled:bg-gray-700 hover:bg-blue-700 transition-colors">
                                    <Send size={18} />
                                </button>
                            </form>
                        </div>
                    </>
                )}
            </div>

            {isNewChatModalOpen && (
                <GenericModal isOpen={isNewChatModalOpen} onClose={closeNewChatModal} header="New Chat">
                    <div className="p-2 space-y-6 bg-white dark:bg-gray-800 rounded-lg">
                        <div>
                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Chat title <span className="text-red-500">*</span></label>
                            <input type="text" value={newChatTitle} onChange={(e) => setNewChatTitle(e.target.value)} placeholder="e.g. Alpha Platform questions" className="w-full p-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white transition-all outline-none" />
                        </div>
                        <div>
                            <div>
                                <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">Chat Scope</label>
                                <div className="space-y-3">
                                    <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${chatScope === "ALL" ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500" : "border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:bg-gray-50 dark:hover:bg-gray-700"}`}>
                                        <input type="radio" name="chatScope" checked={chatScope === "ALL"} onChange={() => { setChatScope("ALL"); setSelectedProjectIds([]); }} className="mt-1 text-blue-600 focus:ring-blue-500" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">All Projects</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">AI can answer questions using documents from all projects you have access to.</p>
                                        </div>
                                    </label>
                                    <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${chatScope === "SELECTED" ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500" : "border-gray-200 dark:border-gray-700 hover:border-blue-300 hover:bg-gray-50 dark:hover:bg-gray-700"}`}>
                                        <input type="radio" name="chatScope" checked={chatScope === "SELECTED"} onChange={() => setChatScope("SELECTED")} className="mt-1 text-blue-600 focus:ring-blue-500" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">Selected Projects</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Choose one or more projects that this chat should use.</p>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            {chatScope === "SELECTED" && (
                                <div>
                                    <label className="block text-sm font-medium mt-2 mb-2 text-gray-700 dark:text-gray-300">Project Names <span className="text-red-500">*</span></label>
                                    <div className="max-h-56 overflow-y-auto space-y-2 pr-2">
                                        {projects.length === 0 ? (
                                            <div className="text-sm text-gray-500 dark:text-gray-400 italic p-2 border border-gray-200 dark:border-gray-800 rounded-lg">No projects assigned to you.</div>
                                        ) : (
                                            projects.map((project: any) => (
                                                <label key={project.projectId} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                                                    <input type="checkbox" checked={selectedProjectIds.includes(project.projectId)} onChange={() => toggleProjectSelection(project.projectId)} className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                                                    <span className="text-sm text-gray-900 dark:text-gray-200 font-medium">{project.name}</span>
                                                </label>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800 mt-6">
                            <button onClick={closeNewChatModal} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700 transition-colors">Cancel</button>
                            <button onClick={handleCreateChat} disabled={isCreateDisabled || creatingChat} className="flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[130px]">
                                {creatingChat ? <><ButtonSpinner /> Creating...</> : <><Plus size={16} /> Create Chat</>}
                            </button>
                        </div>
                    </div>
                </GenericModal>
            )}
            {openDeleteModal && sessionToDelete && (
                <GenericModal
                    isOpen={openDeleteModal}
                    onClose={() => {
                        setOpenDeleteModal(false);
                        setSessionToDelete(null);
                    }}
                    header="Delete Chat"
                >
                    <DeletePopup
                        handleDelete={() => confirmDelete()}
                        onClose={() => {
                            setOpenDeleteModal(false);
                            setSessionToDelete(null);
                        }}
                        selectedRow={{ id: sessionToDelete }}
                    />
                </GenericModal>
            )}
        </div>
    );
}